import { useState } from 'react';
import { submitNewPassword } from 'supertokens-web-js/recipe/emailpassword';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ErrorMessage,
  EmployEngageLogo,
  Button,
} from '@employ-engage/common-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

export const ResetPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    isSubmitting,
  } = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: Yup.object({
      password: Yup.string().required('Required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
        .required('Required'),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      async function resetPassword(newPassword: string) {
        const res = await submitNewPassword({
          formFields: [{ id: 'password', value: newPassword }],
        });

        if (res.status === 'OK') {
          // success – maybe redirect to login
          navigate('/');
        } else if (res.status === 'FIELD_ERROR') {
          // password failed your policy; show res.formFields[0].error
          throw new Error(res.formFields[0].error);
        } else if (res.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
          // token expired / already used – send user back to "forgot password"
          throw new Error('Reset password token is invalid or has expired');
        } else {
          // generic error
          throw new Error('An error occurred while resetting the password');
        }
      }
      try {
        await resetPassword(values.password);
        // Redirect to login or show success message
      } catch (error: any) {
        // Handle error, possibly set form errors
        setErrors({ password: t(error.message) });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="flex justify-center">
          <EmployEngageLogo />
        </div>
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          {t('Reset your password')}
        </h2>
      </div>
      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('Password')}
              </label>
            </div>
            <div className="mt-2">
              <div className="flex">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  autoComplete="current-password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="block w-5/6 rounded-l-lg border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowPassword((prevState) => !prevState);
                  }}
                  className="w-1/6 rounded-r-lg border-0 p-1.5  shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 text-center cursor-pointer bg-gray-100 hover:bg-gray-200"
                >
                  {showPassword ? (
                    <FontAwesomeIcon icon={faEyeSlash} />
                  ) : (
                    <FontAwesomeIcon icon={faEye} />
                  )}
                </button>
              </div>
              {errors.password && touched.password && (
                <ErrorMessage
                  errors={errors.password}
                  touched={touched.password}
                />
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="passwordConfirmation"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('Password Confirmation')}
              </label>
            </div>
            <div className="mt-2">
              <div className="flex">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="block w-5/6 rounded-l-lg border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowPassword((prevState) => !prevState);
                  }}
                  className="w-1/6 rounded-r-lg border-0 p-1.5  shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 text-center cursor-pointer bg-gray-100 hover:bg-gray-200"
                >
                  {showPassword ? (
                    <FontAwesomeIcon icon={faEyeSlash} />
                  ) : (
                    <FontAwesomeIcon icon={faEye} />
                  )}
                </button>
              </div>
              {errors.confirmPassword && touched.confirmPassword && (
                <ErrorMessage
                  errors={errors.confirmPassword}
                  touched={touched.confirmPassword}
                />
              )}
            </div>
          </div>
          <div>
            <Button
              disabled={isSubmitting}
              type="submit"
              className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {isSubmitting ? t('Resetting password...') : t('Reset Password')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
